import { formatDate } from '@angular/common';

/**
 * Per-facility / per-row referral state helpers shared by every referral action
 * popup (Accept, Accept + resident match, Decline, Pending Review / Need More
 * Info). One source of truth for:
 *   • the facility's CURRENT state label ("Accepted", "Pending Review", …) —
 *     Flowtrics' own recorded state wins, then the portal's echoed response;
 *   • the who / when line (respondedBy · respondedOn) shown under the facility;
 *   • "already in this state" — the action would send the SAME response again,
 *     so the facility cannot be selected and the user is told why.
 */
export type ReferralAction = 'accept' | 'decline' | 'pendingReview' | 'needMoreInfo';

export function actionStateLabel(action: ReferralAction | null): string {
  switch (action) {
    case 'accept': return 'Accepted';
    case 'decline': return 'Declined';
    case 'pendingReview': return 'Pending Review';
    case 'needMoreInfo': return 'Need More Info';
    default: return '';
  }
}

/** Workflow value of the status popup (1 = Pending Review, 2 = Need More Info) → action. */
export function actionForWorkflow(value: number): ReferralAction | null {
  if (value === 1) return 'pendingReview';
  if (value === 2) return 'needMoreInfo';
  return null;
}

/** Current state of one recipient facility, '' when nothing is recorded yet. */
export function facilityStateLabel(f: any): string {
  if (!f) return '';
  if (f.localStatus === 1) return 'Accepted';
  if (f.localStatus === 2) return 'Declined';
  if (f.localStatus === 0 && f.localWorkflow === 3) return 'Bed Hold';
  if (f.localStatus === 0 && f.localWorkflow === 1) return 'Pending Review';
  if (f.localStatus === 0 && f.localWorkflow === 2) return 'Need More Info';
  if (f.siblingStatus === 1) return 'Accepted';
  if (f.siblingStatus === 2) return 'Declined';
  const pr = ('' + (f.providerResponse || '')).toLowerCase();
  if (pr) {
    if (pr.indexOf('accept') === 0) return 'Accepted';
    if (pr.indexOf('declin') === 0 || pr.indexOf('denied') >= 0) return 'Declined';
    if (pr.indexOf('more info') >= 0 || pr.indexOf('information') >= 0) return 'Need More Info';
    if (pr.indexOf('pending') >= 0 || pr.indexOf('review') >= 0) return 'Pending Review';
    return f.providerResponse;
  }
  return '';
}

/** "by Jane Doe · Sep 24, 3:10 PM" / "via portal · Sep 24, 3:10 PM" / ''. */
export function facilityWhoWhen(f: any): string {
  if (!f) return '';
  const parts: string[] = [];
  if (f.stateSource === 'Portal') parts.push('via portal');
  else if (f.respondedBy) parts.push('by ' + f.respondedBy);
  if (f.respondedOn) {
    try { parts.push(formatDate(f.respondedOn, 'MMM d, h:mm a', 'en-US')); } catch { /* ignore bad dates */ }
  }
  return parts.join(' · ');
}

/** One line for the facility card: "Accepted · by Jane Doe · Sep 24, 3:10 PM". */
export function facilityStateLine(f: any): string {
  const label = facilityStateLabel(f);
  if (!label) return '';
  const who = facilityWhoWhen(f);
  return who ? label + ' · ' + who : label;
}

/** True when sending this action to the facility would repeat its current state. */
export function facilityAlreadyIn(f: any, action: ReferralAction | null): boolean {
  if (!f || !action) return false;
  const label = facilityStateLabel(f);
  return !!label && label === actionStateLabel(action);
}

/** Tooltip / message for a facility that is already in the action's state. */
export function facilityAlreadyHint(f: any, action: ReferralAction | null): string {
  if (!facilityAlreadyIn(f, action)) return '';
  const who = facilityWhoWhen(f);
  return (f.facilityName || 'This facility') + ' is already ' + actionStateLabel(action)
    + (who ? ' (' + who + ')' : '') + ' — nothing new would be sent.';
}

/** Facilities (from the picker options) that are already in the action's state. */
export function facilitiesAlreadyIn(options: any[], action: ReferralAction | null): any[] {
  return (options || []).filter(f => facilityAlreadyIn(f, action));
}

/** "Santa Clara Post Acute (by Jane · Sep 24, 3:10 PM), Solano Post Acute" */
export function facilitiesAlreadyText(options: any[], action: ReferralAction | null): string {
  return facilitiesAlreadyIn(options, action)
    .map(f => { const w = facilityWhoWhen(f); return (f.facilityName || '') + (w ? ' (' + w + ')' : ''); })
    .join(', ');
}

// ───────── Resident office (where the accepted patient's resident record is created) ─────────
export interface ResidentOfficeOption { officeID: number; officeName: string; facilityName: string; isThisOffice: boolean; }

function sessionOffices(): any[] {
  try { return JSON.parse(sessionStorage.getItem('officeList') || '[]') || []; } catch { return []; }
}

/**
 * Offices where the resident may be created for the SELECTED facilities: each
 * selected facility's mapped office (an unmapped own facility → this office),
 * limited to offices the user can work in. This office is listed first.
 */
export function residentOfficeOptions(selected: any[], myOfficeID: number): ResidentOfficeOption[] {
  const offices = sessionOffices();
  const out: ResidentOfficeOption[] = [];
  const seen: { [id: number]: boolean } = {};
  for (const f of selected || []) {
    let id = Number(f?.officeId || 0);
    if (!id && f?.isMine) id = Number(myOfficeID || 0);
    if (!id || seen[id]) continue;
    const o = offices.find((x: any) => Number(x.officeID) === id);
    if (!o) continue;   // the user cannot work in that office
    seen[id] = true;
    out.push({
      officeID: id,
      officeName: o.officeName || o.name || f.officeName || ('Office ' + id),
      facilityName: f.facilityName || '',
      isThisOffice: id === Number(myOfficeID || 0)
    });
  }
  out.sort((a, b) => (b.isThisOffice ? 1 : 0) - (a.isThisOffice ? 1 : 0));
  return out;
}

/** Selected facilities whose office the user cannot create residents in (unmapped or no access). */
export function facilitiesWithoutResidentOffice(selected: any[], myOfficeID: number): any[] {
  const offices = sessionOffices();
  return (selected || []).filter(f => {
    let id = Number(f?.officeId || 0);
    if (!id && f?.isMine) id = Number(myOfficeID || 0);
    return !id || !offices.some((x: any) => Number(x.officeID) === id);
  });
}

/** Current state of a queue row itself (internal referrals / single-facility rows). */
export function rowStateLabel(row: any): string {
  if (!row) return '';
  const wf = Number(row.referralStatus ?? 0);
  if (wf === 1) return 'Pending Review';
  if (wf === 2) return 'Need More Info';
  if (wf === 3) return 'Bed Hold';
  return 'New';
}

export function rowAlreadyIn(row: any, action: ReferralAction | null): boolean {
  if (!row || !action) return false;
  return rowStateLabel(row) === actionStateLabel(action);
}

// ───────── Portal-style queues (2026-09-28) ─────────
export const QUEUE_DEFS: { queue: number; key: string; label: string }[] = [
  { queue: 1, key: 'new', label: 'New' },
  { queue: 2, key: 'inprogress', label: 'In-Progress' },
  { queue: 3, key: 'bookedhere', label: 'Booked Here' },
  { queue: 4, key: 'bookedelsewhere', label: 'Booked Elsewhere' },
  { queue: 5, key: 'archived', label: 'Archived' },
];
export function queueLabel(queue: number): string {
  const d = QUEUE_DEFS.find(x => x.queue === Number(queue || 0));
  return d ? d.label : '';
}
/** A recipient facility's queue label (state table first, then the portal's placement). */
export function facilityQueueLabel(f: any): string {
  if (!f) return '';
  const q = Number(f.portalQueue || 0);
  if (q > 0) return queueLabel(q);
  if (f.patientPlacedDate || f.patientPlacedOn) return 'Booked Here';
  if (f.isArchived) return 'Archived';
  return '';
}
